import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import clsx from 'clsx';
import { MdSchedule, MdSettings } from 'react-icons/md';
import { Button } from '@/components/Button';
import CustomFieldInput from '@/components/CustomFieldInput';
import DatePickerModal from '@/components/DatePickerModal';
import Loading from '@/components/Loading';
import Select from '@/components/Select';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import useFetchIam from '@/features/agent/api/useFetchIam';
import useActiveUsers from '@/features/agent/active_users/hooks/useActiveUsers';
import { buildCustomDataRecordFromParse } from '@/utils/candidateResumeParse';
import { buildCustomDataPayload, type CustomDataFormValues } from '@/utils/customDataForm';
import { isCustomFieldValueEmpty } from '@/utils/customFieldValidation';
import { datetimeFormat } from '@/utils/dateFormat';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import { candidateInterviewTranscriptAtom } from '../atom/candidatesAtom';
import useFetchCandidateProfile from '../api/useFetchCandidateProfile';
import useFetchInterviewCustomFields from '../api/useFetchInterviewCustomFields';
import useExtractInterviewLogFromTranscript from '../api/useExtractInterviewLogFromTranscript';
import usePostCandidateInterviewLogs from '../api/usePostCandidateInterviewLogs';

type Props = {
  candidateProfileId: number;
  fromTranscript: boolean;
};

const REQUIRED_TAG_CLASS_NAME =
  'inline-flex h-5 items-center rounded-full bg-danger_lighten px-sm text-tag-sm-highlight text-danger';

export default function CandidatesInterviewLogNewTemplate({
  candidateProfileId,
  fromTranscript,
}: Props) {
  useDocumentTitle('面談情報の作成');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: candidateProfile } = useFetchCandidateProfile(candidateProfileId);
  useSetBreadcrumbTitle(candidateProfile?.name);
  const { data: iam } = useFetchIam();
  const { data: activeUsersData } = useActiveUsers();
  const { data: customFieldsData } = useFetchInterviewCustomFields();
  const customFields = customFieldsData?.records;
  const [transcript, setTranscript] = useAtom(candidateInterviewTranscriptAtom);
  const { mutateAsync: extractInterviewLog, isPending: isExtracting } =
    useExtractInterviewLogFromTranscript();
  const { mutateAsync: postInterviewLog, isPending: isPosting } = usePostCandidateInterviewLogs();

  const [interviewedAt, setInterviewedAt] = useState('');
  const [organizationUserId, setOrganizationUserId] = useState('');
  const [customValues, setCustomValues] = useState<CustomDataFormValues>({});
  const extractStartedRef = useRef(false);

  const selectedUserId = organizationUserId || String(iam?.current_user?.id ?? '');
  const isWaitingExtraction = fromTranscript && !!transcript && !customFields;
  const isGenerating = isExtracting || isWaitingExtraction;
  const isRequiredFilled = (customFields ?? [])
    .filter((field) => field.is_required)
    .every((field) => !isCustomFieldValueEmpty(field, customValues[String(field.id)] ?? undefined));
  const canSubmit =
    !isGenerating && !isPosting && !!interviewedAt && !!selectedUserId && isRequiredFilled;

  useEffect(() => {
    if (!fromTranscript || !transcript || !customFields || extractStartedRef.current) return;
    extractStartedRef.current = true;
    setTranscript(null);

    extractInterviewLog({ candidateProfileId, transcript })
      .then((result) => {
        if (result.interviewed_at) setInterviewedAt(result.interviewed_at);
        setCustomValues(buildCustomDataRecordFromParse(result, customFields));
      })
      .catch((error) => {
        showErrorToast(
          error instanceof Error ? error.message : 'AI による面談情報の作成に失敗しました'
        );
      });
  }, [fromTranscript, transcript, customFields]);

  const backToTimeline = () =>
    navigate({
      to: '/agent/candidates/$candidateProfileId',
      params: { candidateProfileId: String(candidateProfileId) },
      search: { tab: 'timeline' },
    });

  const handleSubmit = async () => {
    const customData = buildCustomDataPayload(customValues, 'create');
    try {
      await postInterviewLog({
        candidateProfileId,
        title: '面談',
        interviewed_at: interviewedAt,
        organization_user_id: Number(selectedUserId),
        custom_data: customData.length > 0 ? customData : undefined,
      });
      await queryClient.invalidateQueries({
        queryKey: ['candidateChatComments', candidateProfileId],
      });
      showSuccessToast('面談情報を追加しました');
      backToTimeline();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '面談情報の追加に失敗しました');
    }
  };

  return (
    <div className="flex flex-col p-lg pb-[100px]">
      {isGenerating ? (
        <div className="flex flex-col items-center gap-lg pt-[180px]">
          <Loading size="small" />
          <p className="text-body-md text-main">データから面談情報を作成しています</p>
        </div>
      ) : (
        <div className="flex flex-col gap-[14px] p-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-md">
              <div className="flex w-[216px] flex-col gap-xs">
                <div className="flex items-center gap-xs">
                  <p className="text-heading-xxs text-main">面談日時</p>
                  <span className={REQUIRED_TAG_CLASS_NAME}>必須</span>
                </div>
                <DatePickerModal type="datetime" value={interviewedAt} onChange={setInterviewedAt}>
                  <button
                    type="button"
                    className="flex h-[42px] w-full cursor-pointer items-center justify-between rounded-sm border border-solid border-border bg-white px-sm focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
                  >
                    <span
                      className={clsx(
                        'text-body-sm',
                        interviewedAt ? 'text-main' : 'text-placeholder'
                      )}
                    >
                      {interviewedAt ? datetimeFormat(new Date(interviewedAt)) : 'YYYY/MM/DD 00:00'}
                    </span>
                    <MdSchedule className="size-6 text-main" />
                  </button>
                </DatePickerModal>
              </div>
              <div className="flex w-[210px] flex-col gap-xs">
                <p className="text-heading-xxs text-main">面談者</p>
                <Select
                  items={(activeUsersData?.activeUsers ?? []).map((user) => ({
                    value: String(user.id),
                    label: user.name,
                  }))}
                  value={selectedUserId}
                  onValueChange={setOrganizationUserId}
                  className="w-full"
                />
              </div>
            </div>
            {iam?.current_user?.is_admin && (
              <Button asChild color="tertiary" size="md">
                <Link to="/agent/customize" search={{ type: 'candidate_interview_log' as const }}>
                  <MdSettings className="size-5" />
                  面談項目を変更
                </Link>
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-md gap-y-[14px]">
            {(customFields ?? []).map((field) => (
              <div
                key={field.id}
                className={clsx(
                  'flex flex-col gap-xs',
                  field.field_type === 'long_text' && 'col-span-2'
                )}
              >
                <div className="flex items-center gap-xs">
                  <p className="text-heading-xxs text-main">{field.name}</p>
                  {field.is_required && <span className={REQUIRED_TAG_CLASS_NAME}>必須</span>}
                </div>
                <CustomFieldInput
                  field={field}
                  value={customValues[String(field.id)] ?? undefined}
                  onChange={(value) =>
                    setCustomValues((prev) => ({ ...prev, [String(field.id)]: value }))
                  }
                  className={clsx(
                    'w-full',
                    field.field_type === 'long_text' && 'min-h-[236px] text-body-xs'
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="fixed right-lg bottom-lg flex items-center gap-xs">
        <Button type="button" color="secondary" size="lg" onClick={backToTimeline}>
          キャンセル
        </Button>
        <Button
          type="button"
          color="primary"
          size="lg"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          新規作成
        </Button>
      </div>
    </div>
  );
}
